import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "realignmind-session-theme";
const VALID = new Set(["dark", "light", "system"]);

const SessionThemeContext = createContext({
  theme: "dark",
  setTheme: () => {},
  effectiveTheme: "dark",
});

function readSystemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readStoredTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.has(stored)) return stored;
  } catch {
    // localStorage unavailable; fall through
  }
  return "dark";
}

export function SessionThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [systemPrefersDark, setSystemPrefersDark] = useState(readSystemPrefersDark);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemPrefersDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const setTheme = useCallback((next) => {
    if (!VALID.has(next)) return;
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const effectiveTheme = theme === "system" ? (systemPrefersDark ? "dark" : "light") : theme;

  const value = useMemo(
    () => ({ theme, setTheme, effectiveTheme }),
    [theme, setTheme, effectiveTheme]
  );

  return <SessionThemeContext.Provider value={value}>{children}</SessionThemeContext.Provider>;
}

export function useSessionTheme() {
  return useContext(SessionThemeContext);
}

export function SessionThemeWrapper({ children, className = "", ...rest }) {
  const { effectiveTheme } = useSessionTheme();
  return (
    <div data-session-theme={effectiveTheme} className={className} {...rest}>
      {children}
    </div>
  );
}

export default SessionThemeContext;
